import { useCallback, useState } from 'react';
import { httpGet } from '../../components/utils';

interface CreateGenerateJobResponse {
    jobId: string;
}

interface UseCreateGenerateJobParams<TResponse = { jobId: string }> {
    generateUrl: string;
    queryParams?: Record<string, string>;
    onSuccess?: (response: TResponse) => void;
    onFailure?: (error: unknown) => void;
}

interface UseCreateGenerateJobReturn {
    loading: boolean;
    error: unknown;
    createJob: () => Promise<void>;
    jobId?: string;
}

export const useJobCreation = <TResponse extends CreateGenerateJobResponse = CreateGenerateJobResponse>({
    generateUrl,
    queryParams = {},
    onSuccess,
    onFailure
}: UseCreateGenerateJobParams<TResponse>): UseCreateGenerateJobReturn => {
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<unknown | undefined>();
    const [jobId, setJobId] = useState<string | undefined>();

    const createJob = useCallback(async () => {
        setLoading(true);
        setError(undefined);

        try {
            const fullUrl = `${generateUrl}?${new URLSearchParams(queryParams).toString()}`;
            const response = await httpGet(fullUrl);

            if (response.data) {
                setJobId((response.data as unknown as TResponse).jobId);
                onSuccess?.(response.data as TResponse);
            }
        } catch (err) {
            setError(err);
            onFailure?.(err);
        }

        setLoading(false);
    }, [generateUrl, onFailure, onSuccess, queryParams]);

    return { loading, error, createJob, jobId };
};
